/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import isPlainObject from '@stdlib/assert-is-plain-object';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import { isPrimitive as isPositiveInteger } from '@stdlib/assert-is-positive-integer';
import isndarrayLike from '@stdlib/assert-is-ndarray-like';
import isReadOnly from '@stdlib/ndarray-base-assert-is-read-only';
import hasOwnProp from '@stdlib/assert-has-own-property';
import iteratorSymbol from '@stdlib/symbol-iterator';
import zeros from '@stdlib/array-base-zeros';
import getShape from '@stdlib/ndarray-shape';
import numel from '@stdlib/ndarray-base-numel';
import slice from '@stdlib/ndarray-base-slice';
import { assign as nextCartesianIndex } from '@stdlib/ndarray-base-next-cartesian-index';
import args2multislice from '@stdlib/slice-base-args2multislice';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Returns an iterator which iterates over each subarray in a stack of subarrays.
*
* @param {ndarray} x - input value
* @param {PositiveInteger} ndims - number of dimensions to stack
* @param {Options} [options] - function options
* @param {boolean} [options.readonly=true] - boolean indicating whether returned views should be read-only
* @throws {TypeError} first argument must be an ndarray
* @throws {TypeError} first argument must have at least `ndims+1` dimensions
* @throws {TypeError} second argument must be a positive integer
* @throws {TypeError} options argument must be an object
* @throws {TypeError} must provide valid options
* @throws {Error} cannot write to a read-only array
* @returns {Iterator} iterator
*
* @example
* import array from '@stdlib/ndarray-array';
* import ndarray2array from '@stdlib/ndarray-to-array';
*
* var x = array( [ [ [ 1, 2 ], [ 3, 4 ] ], [ [ 5, 6 ], [ 7, 8 ] ] ] );
* // returns <ndarray>
*
* var iter = nditerSubarrays( x, 2 );
*
* var v = iter.next().value;
* // returns <ndarray>
*
* var arr = ndarray2array( v );
* // returns [ [ 1, 2 ], [ 3, 4 ] ]
*
* v = iter.next().value;
* // returns <ndarray>
*
* arr = ndarray2array( v );
* // returns [ [ 5, 6 ], [ 7, 8 ] ]
*
* // ...
*/
function nditerSubarrays( x, ndims ) {
	var options;
	var shape;
	var opts;
	var iter;
	var FLG;
	var idx;
	var dim;
	var S;
	var M;
	var N;
	var i;

	if ( !isndarrayLike( x ) ) {
		throw new TypeError( format( '1zf4f', x ) );
	}
	if ( !isPositiveInteger( ndims ) ) {
		throw new TypeError( format( '1zf45', ndims ) );
	}
	opts = {
		'writable': false
	};
	if ( arguments.length > 2 ) {
		options = arguments[ 2 ];
		if ( !isPlainObject( options ) ) {
			throw new TypeError( format( '1zf2V', options ) );
		}
		if ( hasOwnProp( options, 'readonly' ) ) {
			if ( !isBoolean( options.readonly ) ) {
				throw new TypeError( format( '1zf2o', 'readonly', options.readonly ) );
			}
			opts.writable = !options.readonly;
			if ( opts.writable && isReadOnly( x ) ) {
				throw new Error( format('1zfF4') );
			}
		}
	}
	// Retrieve input array meta data:
	shape = getShape( x );
	M = shape.length;

	// Ensure that the input array has sufficient dimensions...
	if ( M <= ndims ) {
		throw new TypeError( format( '1zfGa', ndims+1 ) );
	}
	// Check whether the input array is empty...
	N = numel( shape );
	if ( N === 0 ) {
		FLG = true;
	}
	// Compute the number of subarrays across all stacks of subarrays:
	dim = M - ndims - 1;
	for ( i = dim+1; i < M; i++ ) {
		N /= shape[ i ];
	}
	S = shape[ dim ];

	// Initialize an index array for generating slices:
	idx = zeros( M );

	// Set the last `ndims` elements to `null` to indicate that we want a full "slice" for the last `ndims` dimensions:
	for ( i = dim+1; i < M; i++ ) {
		idx[ i ] = null;
	}
	// Initialize a counter:
	i = -1;

	// Create an iterator protocol-compliant object:
	iter = {};
	setReadOnly( iter, 'next', next );
	setReadOnly( iter, 'return', end );

	// If an environment supports `Symbol.iterator`, make the iterator iterable:
	if ( iteratorSymbol ) {
		setReadOnly( iter, iteratorSymbol, factory );
	}
	return iter;

	/**
	* Returns an iterator protocol-compliant object containing the next iterated value.
	*
	* @private
	* @returns {Object} iterator protocol-compliant object
	*/
	function next() {
		var s;
		var j;

		i += 1;
		if ( FLG || i >= N ) {
			return {
				'done': true
			};
		}
		// Create a multi-slice for the current view:
		s = args2multislice( idx );

		// Update the index array:
		j = ( idx[ dim ] + 1 ) % S;
		idx[ dim ] = j;
		if ( j === 0 ) {
			// If we've iterated over all the subarrays in the current stack, move on to the next set of subarrays:
			idx = nextCartesianIndex( shape, 'row-major', idx, dim-1, idx );
		}
		// Return the next slice:
		return {
			'value': slice( x, s, true, opts.writable ),
			'done': false
		};
	}

	/**
	* Finishes an iterator.
	*
	* @private
	* @param {*} [value] - value to return
	* @returns {Object} iterator protocol-compliant object
	*/
	function end( value ) {
		FLG = true;
		if ( arguments.length ) {
			return {
				'value': value,
				'done': true
			};
		}
		return {
			'done': true
		};
	}

	/**
	* Returns a new iterator.
	*
	* @private
	* @returns {Iterator} iterator
	*/
	function factory() {
		return nditerSubarrays( x, ndims, opts );
	}
}


// EXPORTS //

export default nditerSubarrays;
